import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  HostListener
} from '@angular/core';

import jsPDF from 'jspdf';
import { CartService } from 'src/app/services/cart.service';
import { ProductsService } from 'src/app/services/product.service';

@Component({
  selector: 'app-products',
  templateUrl: './products.component.html',
  styleUrls: ['./products.component.css']
})
export class ProductsComponent implements OnInit, AfterViewInit, OnDestroy {

  // =========================================================
  // PRODUCTS
  // =========================================================
  products: any[] = [];
  categories: string[] = [];
  bestSellers: any[] = [];

  // =========================================================
  // SEARCH / FILTER
  // =========================================================
  searchTerm: string = '';
  selectedCategory: string = 'All';
  sortBy: string = 'popular';

  priceRangeMin: number = 0;
  priceRangeMax: number = 150;
  minPrice: number = 0;
  maxPrice: number = 150;
  // =========================================================
  // PAGINATION
  // =========================================================
  currentPage: number = 1;
  itemsPerPage: number = 8;

  // =========================================================
  // CART
  // =========================================================
  cartItems: any[] = [];
  totalPrice: number = 0;
  isCartOpen: boolean = false;

  // =========================================================
  // CONSTRUCTOR
  // =========================================================
  constructor(
    private productService: ProductsService,
    private cartService: CartService
  ) {}

  // =========================================================
  // INIT
  // =========================================================
  ngOnInit(): void {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });

    this.setItemsPerPage();

    this.productService.getProducts().subscribe((products: any[]) => {
      this.products = (products || []).map(product => {
        product.options = Array.isArray(product.options) ? product.options : [];
        product.selectedOption = product.options.length > 0 ? product.options[0] : null;
        return product;
      });

      // =====================================================
      // CATEGORIES
      // =====================================================
      this.categories = this.products
        .map(product => product.category)
        .filter((category, index, array) => category && array.indexOf(category) === index);

      // =====================================================
      // BEST SELLERS
      // =====================================================
      this.bestSellers = this.products
        .filter(product => product.bestSeller)
        .slice(0, 3);

      /*
       * Products are rendered through *ngFor after the subscription completes.
       * Therefore we refresh the reveal observer after Angular has had a chance to render them.
       */
      this.refreshRevealAnimations();
    });

    this.syncCart();
  }

  // =========================================================
  // RESPONSIVE PAGINATION
  // =========================================================
  @HostListener('window:resize')
  onResize(): void {
    const oldItemsPerPage = this.itemsPerPage;
    this.setItemsPerPage();

    if (oldItemsPerPage !== this.itemsPerPage) {
      this.currentPage = 1;
      /*
       * Pagination can change the number of product cards rendered by *ngFor.
       */
      this.refreshRevealAnimations();
    }
  }

  setItemsPerPage(): void {
    const width = window.innerWidth;
    
    // Mobile & Tablet
    if (width <= 1024) {
      this.itemsPerPage = 6;
    } 
    // Desktop
    else {
      this.itemsPerPage = 8;
    }
  }

  // =========================================================
  // SEARCH
  // =========================================================
  onSearchChange(): void {
    this.currentPage = 1;
    this.refreshRevealAnimations();
  }

  // =========================================================
  // CATEGORY
  // =========================================================
  selectCategory(category: string): void {
    this.selectedCategory = category;
    this.currentPage = 1;
    this.refreshRevealAnimations();
  }

  // =========================================================
  // CATEGORY COUNT
  // =========================================================
  getCategoryCount(category: string): number {
    return this.products.filter(product => product.category === category).length;
  }

  // =========================================================
  // PRICE
  // =========================================================
  onPriceChange(): void {
    /* Make sure values are numbers. */
    this.minPrice = Number(this.minPrice);
    this.maxPrice = Number(this.maxPrice);

    /* Prevent minimum from exceeding maximum. */
    if (this.minPrice > this.maxPrice) {
      this.minPrice = this.maxPrice;
    }
  }

  applyPriceFilter(): void {
    this.currentPage = 1;
    this.refreshRevealAnimations();
  }

  // =========================================================
  // FILTERED PRODUCTS
  // =========================================================
  get filteredProducts(): any[] {
    let result = [...this.products];

    // =======================================================
    // SEARCH
    // =======================================================
    if (this.searchTerm && this.searchTerm.trim()) {
      const term = this.searchTerm.toLowerCase().trim();
      
      result = result.filter(product => {
        const name = String(product.name || '').toLowerCase();
        const description = String(product.description || '').toLowerCase();
        const category = String(product.category || '').toLowerCase();

        return (
          name.includes(term) ||
          description.includes(term) ||
          category.includes(term)
        );
      });
    }

    // =======================================================
    // CATEGORY
    // =======================================================
    if (this.selectedCategory !== 'All') {
      result = result.filter(product => product.category === this.selectedCategory);
    }

    // =======================================================
    // PRICE
    // =======================================================
    result = result.filter(product => {
      const price = this.getProductPrice(product);
      return (
        price >= Number(this.minPrice) &&
        price <= Number(this.maxPrice)
      );
    });

    // =======================================================
    // SORT
    // =======================================================
    if (this.sortBy === 'priceLow') {
      result.sort((a, b) => this.getProductPrice(a) - this.getProductPrice(b));
    } else if (this.sortBy === 'priceHigh') {
      result.sort((a, b) => this.getProductPrice(b) - this.getProductPrice(a));
    } else if (this.sortBy === 'name') {
      result.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
    } else if (this.sortBy === 'popular') {
      result.sort((a, b) => Number(b.popularity || 0) - Number(a.popularity || 0));
    }

    return result;
  }

  // =========================================================
  // PRODUCT PRICE
  // =========================================================
  private getProductPrice(product: any): number {
    if (product && product.selectedOption && product.selectedOption.price !== undefined) {
      return Number(product.selectedOption.price);
    }
    if (product && Array.isArray(product.options) && product.options.length > 0) {
      return Number(product.options[0].price || 0);
    }
    return 0;
  }

  // =========================================================
  // PAGED PRODUCTS
  // =========================================================
  get pagedProducts(): any[] {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    return this.filteredProducts.slice(start, start + this.itemsPerPage);
  }

  // =========================================================
  // TOTAL PAGES
  // =========================================================
  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredProducts.length / this.itemsPerPage));
  }

  // =========================================================
  // PAGINATION BUTTONS
  // =========================================================
  get paginationPages(): (number | string)[] {
    const total = this.totalPages;
    const current = this.currentPage;
    const pages: (number | string)[] = [];

    if (total <= 7) {
      for (let i = 1; i <= total; i++) {
        pages.push(i);
      }
      return pages;
    }

    // FIRST PAGE
    pages.push(1);

    // LEFT DOTS
    if (current > 4) {
      pages.push('...');
    }

    // MIDDLE PAGES
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    // RIGHT DOTS
    if (current < total - 3) {
      pages.push('...');
    }

    // LAST PAGE
    pages.push(total);
    return pages;
  }

  // =========================================================
  // GO TO PAGE
  // =========================================================
  goToPage(page: number | string): void {
    if (page === '...') {
      return;
    }

    const newPage = Number(page);
    if (newPage < 1 || newPage > this.totalPages) {
      return;
    }

    this.currentPage = newPage;

    /* Wait for Angular to destroy/create the correct product-card elements before refreshing reveals. */
    this.refreshRevealAnimations();

    const element = document.querySelector('.product-area');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }

  // =========================================================
  // NEXT & PREVIOUS PAGE
  // =========================================================
  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.goToPage(this.currentPage + 1);
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    }
  }

  // =========================================================
  // SORT
  // =========================================================
  onSortChange(): void {
    this.currentPage = 1;
    this.refreshRevealAnimations();
  }

  // =========================================================
  // CLEAR FILTERS
  // =========================================================
  clearFilters(): void {
    this.searchTerm = '';
    this.selectedCategory = 'All';
    this.minPrice = this.priceRangeMin;
    this.maxPrice = this.priceRangeMax;
    this.sortBy = 'popular';
    this.currentPage = 1;

    this.refreshRevealAnimations();
  }

  // =========================================================
  // PRODUCT OPTION CHANGE
  // =========================================================
  onOptionChange(product: any): void {
    if (!product || !product.selectedOption) {
      return;
    }

    /* Reset page because the selected option can affect the active price filtering. */
    this.currentPage = 1;
    this.refreshRevealAnimations();
  }

  // =========================================================
  // INCREASE QUANTITY
  // =========================================================
  increaseQty(product: any): void {
    if (!product || !product.selectedOption) {
      return;
    }

    this.cartService.addToCart({
      name: product.name,
      price: Number(product.selectedOption.price),
      quantity: product.selectedOption.label
    });
    this.syncCart();
  }

  // =========================================================
  // DECREASE QUANTITY
  // =========================================================
  decreaseQty(product: any): void {
    if (!product || !product.selectedOption) {
      return;
    }

    this.cartService.decreaseQty({
      name: product.name,
      quantity: product.selectedOption.label
    });
    this.syncCart();
  }

  // =========================================================
  // GET PRODUCT QUANTITY
  // =========================================================
  getQty(product: any): number {
    if (!product || !product.selectedOption) {
      return 0;
    }

    const item = this.cartItems.find(
      item => item.name === product.name && item.quantity === product.selectedOption.label
    );

    return item ? Number(item.qty) : 0;
  }

  // =========================================================
  // SYNC CART
  // =========================================================
  private syncCart(): void {
    this.cartItems = this.cartService.getCart() || [];
    this.totalPrice = this.cartItems.reduce(
      (sum, item) => sum + (Number(item.price || 0) * Number(item.qty || 0)),
      0
    );
  }

  // =========================================================
  // TOTAL QUANTITY
  // =========================================================
  getTotalQuantity(): number {
    return this.cartItems.reduce((total, item) => total + Number(item.qty || 0), 0);
  }

  // =========================================================
  // CART TOGGLE
  // =========================================================
  toggleCart(): void {
    this.isCartOpen = !this.isCartOpen;
  }


  // =========================================================
  // CART ITEM +
  // =========================================================

  increaseCartItem(item: any): void {
    if (!item) {
      return;
    }

    this.cartService.addToCart({
      name: item.name,
      price: Number(item.price),
      quantity: item.quantity || ''
    });

    this.syncCart();
  }


  // =========================================================
  // CART ITEM -
  // =========================================================

  decreaseCartItem(item: any): void {
    if (!item) {
      return;
    }

    this.cartService.decreaseQty({
      name: item.name,
      quantity: item.quantity || ''
    });

    this.syncCart();
  }
  // =========================================================
  // WHATSAPP
  // =========================================================
  sendCartToWhatsApp(): void {
    /*
     * IMPORTANT: Replace this with your actual WhatsApp number.
     * Malaysia example: 60123456789
     * Do NOT include +, spaces or dashes.
     */
    const phoneNumber = '918284948635';

    if (this.cartItems.length === 0) {
      alert('Your cart is empty!');
      return;
    }

    let message = '🛒 *MY ORDER*%0A%0A';
    let total = 0;

    this.cartItems.forEach(item => {
      const itemTotal = Number(item.price || 0) * Number(item.qty || 0);
      message += `• ${item.name}%0A`;
      message += `  ${item.quantity} × ${item.qty} = RM ${itemTotal.toFixed(2)}%0A%0A`;
      total += itemTotal;
    });

    message += `💰 *TOTAL: RM ${total.toFixed(2)}*%0A%0A`;
    message += 'Please confirm my order. Thank you! ☕';

    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
  }

  // =========================================================
  // PDF BILL
  // =========================================================
  downloadPDF(): void {
    if (this.cartItems.length === 0) {
      alert('Your cart is empty!');
      return;
    }

    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text('My Coffee Order', 15, 20);
    doc.setFontSize(10);

    let y = 35;
    let total = 0;

    this.cartItems.forEach(item => {
      const itemTotal = Number(item.price || 0) * Number(item.qty || 0);
      const line = `${item.name} - ${item.quantity} x ${item.qty} = RM ${itemTotal.toFixed(2)}`;

      /* Start a new page before writing outside the printable area. */
      if (y > 270) {
        doc.addPage();
        y = 20;
      }

      doc.text(line, 15, y);
      y += 10;
      total += itemTotal;
    });

    if (y > 270) {
      doc.addPage();
      y = 20;
    }

    doc.setFontSize(14);
    doc.text(`Total: RM ${total.toFixed(2)}`, 15, y + 10);
    doc.save('Liberica-Order-Bill.pdf');
  }

  // =========================================================
  // SMOOTH SCROLL
  // =========================================================
  scrollTo(element: HTMLElement): void {
    if (!element) {
      return;
    }

    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }

  // =========================================================
  // REVEAL ANIMATION SYSTEM
  // =========================================================

  /** IntersectionObserver used for scroll reveal animations. */
  private revealObserver: IntersectionObserver | null = null;

  /** Timer used to wait until Angular has finished rendering dynamic *ngFor / *ngIf elements. */
  private revealRefreshTimer: any = null;

  /** All reveal classes used by the products page. */
  private readonly revealSelectors: string = [
    '.reveal', '.reveal-left', '.reveal-right', '.reveal-up', '.reveal-down', 
    '.reveal-zoom', '.reveal-zoom-out', '.reveal-rotate', '.reveal-slide', 
    '.reveal-fade', '.reveal-scale-up', '.reveal-scale-down', '.reveal-bottom-blur', 
    '.reveal-top-blur', '.reveal-swing', '.reveal-pop', '.reveal-flip-up', 
    '.reveal-flip-side', '.reveal-curtain-up', '.reveal-curtain-left', 
    '.reveal-diagonal', '.reveal-float', '.reveal-elastic', '.reveal-focus', 
    '.reveal-tracking'
  ].join(', ');


  // =========================================================
  // AFTER VIEW INIT
  // =========================================================
  ngAfterViewInit(): void {
    /* Wait until Angular has finished rendering the initial DOM. */
    this.refreshRevealAnimations();
  }

  // =========================================================
  // DESTROY
  // =========================================================
  ngOnDestroy(): void {
    /* Stop observing elements. */
    if (this.revealObserver) {
      this.revealObserver.disconnect();
      this.revealObserver = null;
    }

    /* Clear pending refresh timer. */
    if (this.revealRefreshTimer) {
      clearTimeout(this.revealRefreshTimer);
      this.revealRefreshTimer = null;
    }
  }

  // =========================================================
  // REFRESH REVEAL ANIMATIONS
  // =========================================================
  private refreshRevealAnimations(): void {
    /* Cancel previous scheduled refresh. */
    if (this.revealRefreshTimer) {
      clearTimeout(this.revealRefreshTimer);
    }

    /* Give Angular time to finish rendering *ngFor / *ngIf elements. */
    this.revealRefreshTimer = setTimeout(() => {
      this.revealRefreshTimer = null;
      this.initRevealAnimations();
    }, 0);
  }

  // =========================================================
  // INIT REVEAL ANIMATIONS
  // =========================================================
  private initRevealAnimations(): void {
    /* Check browser support. */
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    /* Find all reveal elements currently rendered inside the products page. */
    const elements = document.querySelectorAll('.products-page ' + this.revealSelectors);

    /* If IntersectionObserver isn't supported, immediately show everything. */
    if (!('IntersectionObserver' in window)) {
      elements.forEach((element: Element) => {
        element.classList.add('active');
      });
      return;
    }

    /* Create observer only once. */
    if (!this.revealObserver) {
      this.revealObserver = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
        entries.forEach(entry => {
          /* Ignore elements that are not currently visible. */
          if (!entry.isIntersecting) {
            return;
          }

          const element = entry.target;
          /* Trigger CSS animation. */
          element.classList.add('active');

          /* Stop observing this element after it has animated. */
          if (this.revealObserver) {
            this.revealObserver.unobserve(element);
          }
        });
      }, {
        threshold: 0.12,
        rootMargin: '0px 0px -60px 0px'
      });
    }

    /* Observe newly rendered elements. Existing elements that already have "active" are ignored. */
    elements.forEach((element: Element) => {
      if (element.classList.contains('active')) {
        return;
      }
      
      if (this.revealObserver) {
        this.revealObserver.observe(element);
      }
    });
  }

  get displayedEnd(): number {
  return Math.min(
    this.currentPage * this.itemsPerPage,
    this.filteredProducts.length
  );
 }

 selectedProductImage: string = '';
 openProductImage(product: any): void {
  this.selectedProductImage = product.image;
 }
 closeProductImage(): void {
  this.selectedProductImage = '';
 }
}